import React, { useState, useEffect, useCallback } from 'react';
import { Card, Accordion, Form, Button, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Função auxiliar para definir a cor do badge de gravidade
const getGravidadeBadge = (gravidade) => {
    switch (gravidade) {
        case 'Grave': return 'danger';
        case 'Moderada': return 'warning';
        case 'Leve': return 'info';
        default: return 'secondary';
    }
};

const AlergiasTab = ({ pacienteId }) => {
    const { currentEntidade } = useEntidadeContext();
    const [alergias, setAlergias] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isFormDirty, setIsFormDirty] = useState(false);

    const initialAlergiaState = {
        id: `new_${Date.now()}`, // ID temporário para novos itens
        substancia: '',
        reacao: '',
        gravidade: 'Leve',
        data_registro: new Date(),
        confirmada_por: '',
        status: 'Ativa',
        observacoes: '',
    };

    // Busca as alergias do paciente no backend
    const fetchAlergias = useCallback(async () => {
        if (!pacienteId) return;
        setIsLoading(true);
        setError(null);
        try {
            // BACKEND: Endpoint para buscar as alergias de um paciente
            const response = await axios.get(`/api/pacientes/${pacienteId}/alergias`);
            const loadedAlergias = (response.data.alergias || []).map(a => ({
                ...a,
                data_registro: a.data_registro ? new Date(a.data_registro) : new Date(),
            }));
            // Se não houver alergias, inicia com um campo em branco
            setAlergias(loadedAlergias.length > 0 ? loadedAlergias : [initialAlergiaState]);
        } catch (err) {
            console.error("Erro ao buscar alergias:", err);
            setError("Não foi possível carregar as alergias do paciente.");
            setAlergias([initialAlergiaState]);
        } finally {
            setIsLoading(false);
            setIsFormDirty(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        fetchAlergias();
    }, [fetchAlergias]);

    // Atualiza o estado de uma alergia específica
    const handleAlergiaChange = (index, field, value) => {
        const newAlergias = [...alergias];
        newAlergias[index][field] = value;
        setAlergias(newAlergias);
        setIsFormDirty(true);
    };

    // Adiciona um novo campo de alergia em branco
    const handleAddAlergia = () => {
        setAlergias([...alergias, { ...initialAlergiaState, id: `new_${Date.now()}` }]);
        setIsFormDirty(true);
    };

    // Remove um registro de alergia
    const handleRemoveAlergia = (index) => {
        if (alergias.length <= 1) {
            setAlergias([initialAlergiaState]);
        } else {
            const newAlergias = [...alergias];
            newAlergias.splice(index, 1);
            setAlergias(newAlergias);
        }
        setIsFormDirty(true);
    };

    // Salva todos os registros de alergia no backend
    const handleSalvarAlergias = async () => {
        if (!pacienteId || !currentEntidade) {
            setError("Informações do paciente ou entidade não encontradas.");
            return;
        }
        
        const invalidAlergia = alergias.find(a => !a.substancia.trim() && (a.reacao.trim() || a.observacoes.trim()));
        if (invalidAlergia) {
            setError("Por favor, preencha o campo 'Substância Alérgica' para cada alergia registrada.");
            return;
        }

        setIsSaving(true);
        setError(null);

        const alergiasParaSalvar = alergias.filter(a => a.substancia.trim() !== '');

        try {
            // BACKEND: Endpoint para salvar/atualizar as alergias de um paciente
            await axios.post(`/api/pacientes/${pacienteId}/alergias`, {
                entidade_id: currentEntidade.id,
                alergias: alergiasParaSalvar,
            });
            
            await fetchAlergias(); // Re-busca os dados para obter IDs atualizados
        } catch (err) {
            const errorMessage = err.response?.data?.message || "Ocorreu um erro ao salvar as alergias.";
            setError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div className="text-center my-5"><Spinner animation="border" /></div>;
    }

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span><FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" /> Registro de Alergias</span>
                <Button variant="primary" onClick={handleSalvarAlergias} disabled={isSaving || !isFormDirty}>
                    {isSaving ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar Alergias'}
                </Button>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                <Accordion defaultActiveKey="0">
                    {alergias.map((alergia, index) => (
                        <Accordion.Item eventKey={String(index)} key={alergia.id}>
                            <Accordion.Header>
                                <span className="flex-grow-1">{`#${index + 1}: ${alergia.substancia || 'Nova Alergia'}`}</span>
                                {alergia.gravidade && <Badge bg={getGravidadeBadge(alergia.gravidade)} className="ms-2">{alergia.gravidade}</Badge>}
                            </Accordion.Header>
                            <Accordion.Body>
                                <Form>
                                    <Row>
                                        <Col md={8}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Substância Alérgica *</Form.Label>
                                                <Form.Control type="text" value={alergia.substancia} onChange={(e) => handleAlergiaChange(index, 'substancia', e.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Gravidade da Reação</Form.Label>
                                                <Form.Select value={alergia.gravidade} onChange={(e) => handleAlergiaChange(index, 'gravidade', e.target.value)}>
                                                    <option value="Leve">Leve</option>
                                                    <option value="Moderada">Moderada</option>
                                                    <option value="Grave">Grave</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Reação (Sintomas)</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={alergia.reacao} onChange={(e) => handleAlergiaChange(index, 'reacao', e.target.value)} />
                                    </Form.Group>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Data de Registro</Form.Label>
                                                <DatePicker selected={alergia.data_registro} onChange={(date) => handleAlergiaChange(index, 'data_registro', date)} dateFormat="dd/MM/yyyy" className="form-control" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Confirmada por</Form.Label>
                                                <Form.Control type="text" value={alergia.confirmada_por} onChange={(e) => handleAlergiaChange(index, 'confirmada_por', e.target.value)} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select value={alergia.status} onChange={(e) => handleAlergiaChange(index, 'status', e.target.value)}>
                                                    <option value="Ativa">Ativa</option>
                                                    <option value="Resolvida">Resolvida</option>
                                                    <option value="Não Confirmada">Não Confirmada</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Observações</Form.Label>
                                        <Form.Control as="textarea" rows={2} value={alergia.observacoes} onChange={(e) => handleAlergiaChange(index, 'observacoes', e.target.value)} />
                                    </Form.Group>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveAlergia(index)}>
                                        <FontAwesomeIcon icon={faTrash} className="me-2" /> Remover Registro
                                    </Button>
                                </Form>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
                <Button variant="outline-primary" className="mt-3 w-100" onClick={handleAddAlergia}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Adicionar Novo Registro de Alergia
                </Button>
            </Card.Body>
        </Card>
    );
};

export default AlergiasTab;
